import React, { useCallback, useState, useEffect } from 'react';
import MenuIcon from '@mui/icons-material/Menu';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import {
    Drawer,
    DrawerBody,
    DrawerFooter,
    DrawerHeader,
    DrawerNavGroup,
    DrawerSubheader,
    NavItem,
} from '@brightlayer-ui/react-components/core/Drawer';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useLanguageLocale } from '../../hooks/language-locale-hooks';
import * as PXBColors from '@brightlayer-ui/colors';
import eatonlogo from '../../assets/images/eaton_logo.svg';
import headerBackgroundImage from '../../assets/images/topology.png';
import { Icon } from '../../lib/icons';
import { AppRoute, addLocationId } from '../../routes';
import { pushAppRoute } from '../../data/domain/route-manager';
import { Actions } from '../../actions';
import { Selectors } from '../../selectors';
import { useCurrentAppRoute } from '../../hooks/app-route-hooks';
import { OrganizationDropdownToolbar } from '../OrganizationDropdownToolbar/OrganizationDropdownToolbar';
import BrandLogo from '../../assets/images/Logo_Blue_Screen.png';
import { Typography } from '@mui/material';

import makeStyles from '@mui/styles/makeStyles';

import { config } from '../../app-config';
import { isRecordFound, useSelectedOrganizationData, useUserRoles } from '../../hooks/data-hooks';
import { useLocationRefreshData } from '../../data/DataProviders/LocationListDataProvider';
import { UserOperations, UserRole } from '../../types/user';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';

const useStyles = makeStyles(() => ({
    background: {
        backgroundImage: `url(${headerBackgroundImage})`,
        backgroundColor: 'rgb(0, 123, 193)',
        backgroundBlendMode: 'soft-light',
        backgroundSize: 'cover',
    },
}));

type AppRouteType = Pick<AppRoute, 'type'>;
const isActive = (requestedAppRoutes: Array<Pick<AppRoute, 'type'>>, currentAppRoute: AppRoute): boolean =>
    requestedAppRoutes.some((requestedAppRoute) => currentAppRoute.type === requestedAppRoute.type);

const OverviewRoutes: AppRouteType[] = [
    { type: 'Overview' },
    { type: 'OverviewOrganization' },
    { type: 'OverviewLocation' },
    { type: 'Timeline' },
    { type: 'TimelineOrganization' },
    { type: 'TimelineLocation' },
    { type: 'Devices' },
    { type: 'DevicesOrganization' },
    { type: 'DevicesLocation' },
    { type: 'Settings' },
    { type: 'MyProfile' },
    { type: 'ManageUser' },
    { type: 'ManageLocation' },
    { type: 'ManageAlarms' },
    { type: 'ManageDeviceProfiles' },
    { type: 'Legal' },
];

export const SideNav: React.FC = () => {
    const classes = useStyles();
    const history = useHistory();
    const dispatch = useDispatch();
    const { t } = useLanguageLocale();
    const selectedOrganization = useSelectedOrganizationData();
    const open = useSelector(Selectors.Navigation.isDrawerOpen);
    const variant = useSelector(Selectors.Navigation.isDrawerVariant);
    const currentAppRoute = useCurrentAppRoute();
    const [active, setActive] = useState('');
    const [orgId, setOrgId] = useState('');
    const userRole = useUserRoles();
    const locationRefreshData = useLocationRefreshData();
    const isLocationRefresh = useSelector(Selectors.Dashboard.isRefresh);
    const matches = useMediaQuery('(max-width:1280px)');

    const setActiveNav = (): void => {
        const routeType = currentAppRoute.type;
        switch (routeType) {
            case 'Overview':
            case 'OverviewOrganization':
            case 'OverviewLocation':
                setActive(t('DRAWER.OVERVIEW'));
                break;
            case 'Timeline':
            case 'TimelineLocation':
            case 'TimelineOrganization':
                setActive(t('DRAWER.TIMELINE'));
                break;
            case 'Devices':
            case 'DevicesOrganization':
            case 'DevicesLocation':
                setActive(t('DRAWER.DEVICES'));
                break;
            case 'Settings':
                setActive(t('DRAWER.SETTINGS'));
                break;
            case 'MyProfile':
                setActive(t('DRAWER.MYPROFILE'));
                break;
            case 'ManageUser':
                setActive(t('DRAWER.MANAGEUSER'));
                break;
            case 'ManageLocation':
                setActive(t('DRAWER.MANAGELOCATION'));
                break;
            case 'ManageAlarms':
                setActive(t('DRAWER.MANAGEALARMS'));
                break;
            case 'ManageDeviceProfiles':
                setActive(t('DRAWER.MANAGEDEVICEPROFILES'));
                break;
            case 'Legal':
                setActive(t('DRAWER.LEGAL'));
                break;
            default:
                setActive('');
        }
    };

    useEffect(() => {
        setActiveNav();
        const activeRoute = isActive(OverviewRoutes, currentAppRoute);
        if (matches && active !== '') dispatch(Actions.Navigation.drawerToggled(false, 'persistent'));
        else dispatch(Actions.Navigation.drawerToggled(activeRoute, activeRoute ? 'persistent' : 'temporary'));
    }, [currentAppRoute, matches]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (isRecordFound(selectedOrganization)) setOrgId(selectedOrganization.value.id);
    }, [selectedOrganization]);

    const hasNoOperation = isRecordFound(userRole) ? userRole.value.every((x) => x.operations.length === 0) : false;

    const isEatonAdmin = isRecordFound(userRole)
        ? userRole.value.length === 1 && userRole.value[0].roleName === UserRole.EatonAdmin
        : false;
    const hasUserManagementAccess = isRecordFound(userRole)
        ? userRole.value.some((x) => x.operations.some((operation) => operation === UserOperations.UserManagement))
        : false;

    const hasSiteManagementAccess = isRecordFound(userRole)
        ? userRole.value.some((x) => x.operations.some((operation) => operation === UserOperations.SiteManagement))
        : false;
    const routeTo = useCallback(
        (destinationAppRoute: AppRoute) => {
            if ('locationId' in currentAppRoute) {
                pushAppRoute(history, addLocationId(destinationAppRoute, currentAppRoute.locationId));
            } else {
                pushAppRoute(history, destinationAppRoute);
            }
        },
        [history, currentAppRoute]
    );

    const toggleDrawer = (): void => {
        dispatch(Actions.Navigation.drawerToggled(!open, 'persistent'));
    };

    const element = window.document.getElementById('root');
    const firstNavList: NavItem[] = [
        {
            itemID: t('DRAWER.OVERVIEW'),
            title: t('DRAWER.OVERVIEW'),
            icon: <Icon.Apps />,
            onClick: (): void => {
                if (isLocationRefresh) locationRefreshData();
                routeTo({ type: 'Overview' });
                if (element !== null) {
                    element.scrollTo({
                        top: 0,
                        behavior: 'smooth',
                    });
                }
            },
            divider: false,
        },
        {
            itemID: t('DRAWER.TIMELINE'),
            title: t('DRAWER.TIMELINE'),
            icon: <Icon.FormatListBulleted />,
            onClick: (): void => {
                if (isLocationRefresh) locationRefreshData();
                routeTo({ type: 'Timeline' });
                if (element !== null) {
                    element.scrollTo({
                        top: 0,
                        behavior: 'smooth',
                    });
                }
            },
            divider: false,
        },
        {
            itemID: t('DRAWER.DEVICES'),
            title: t('DRAWER.DEVICES'),
            icon: <Icon.Device />,
            onClick: (): void => {
                if (isLocationRefresh) locationRefreshData();
                routeTo({ type: 'Devices' });
                if (element !== null) {
                    element.scrollTo({
                        top: 0,
                        behavior: 'smooth',
                    });
                }
            },

            divider: false,
        },
    ];

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const navListForSiteRole: NavItem[] = [
        {
            itemID: t('DRAWER.SETTINGS'),
            title: t('DRAWER.SETTINGS'),
            icon: <Icon.Settings />,
            onClick: (): void => {
                routeTo({ type: 'Settings' });
            },
            divider: false,
        },
        {
            itemID: t('DRAWER.LEGAL'),
            title: t('DRAWER.LEGAL'),
            icon: <Icon.Gavel />,
            onClick: (): void => {
                routeTo({ type: 'Legal' });
            },

            divider: false,
        },
    ];
    const secondNavList: NavItem[] = [
        {
            itemID: t('DRAWER.SETTINGS'),
            title: t('DRAWER.SETTINGS'),
            icon: <Icon.Settings />,

            expandIcon: <ExpandMoreIcon />,
            collapseIcon: <ExpandLessIcon />,
            divider: false,
            items: [
                {
                    itemID: t('DRAWER.MYPROFILE'),
                    title: t('DRAWER.MYPROFILE'),
                    onClick: (): void => {
                        routeTo({ type: 'MyProfile' });
                    },
                },
                {
                    itemID: t('DRAWER.MANAGEUSER'),
                    title: t('DRAWER.MANAGEUSER'),
                    onClick: (): void => {
                        routeTo({ type: 'ManageUser', orgId: orgId });
                    },
                    hidden: !hasUserManagementAccess,
                },
                {
                    itemID: t('DRAWER.MANAGELOCATION'),
                    title: t('DRAWER.MANAGELOCATION'),
                    onClick: (): void => {
                        routeTo({ type: 'ManageLocation', orgId: orgId });
                    },
                    hidden: !hasSiteManagementAccess,
                },
                {
                    itemID: t('DRAWER.MANAGEALARMS'),
                    title: t('DRAWER.MANAGEALARMS'),
                    onClick: (): void => {
                        routeTo({ type: 'ManageAlarms', orgId: orgId });
                    },
                    hidden: !hasSiteManagementAccess || !isEatonAdmin,
                },
                {
                    itemID: t('DRAWER.MANAGEDEVICEPROFILES'),
                    title: t('DRAWER.MANAGEDEVICEPROFILES'),
                    onClick: (): void => {
                        routeTo({ type: 'ManageDeviceProfiles', orgId: orgId });
                    },
                    hidden: !hasSiteManagementAccess || !isEatonAdmin,
                },
            ],
        },
        {
            itemID: t('DRAWER.LEGAL'),
            title: t('DRAWER.LEGAL'),
            icon: <Icon.Gavel />,
            onClick: (): void => {
                routeTo({ type: 'Legal' });
            },

            divider: false,
        },
    ];
    return (
        <>
            <Drawer
                variant={variant}
                data-testid="side-nav-drawer"
                activeItem={active}
                open={open}
                ModalProps={{
                    onClose: (): void => {
                        toggleDrawer();
                    },
                }}
            >
                <DrawerHeader
                    fontColor={PXBColors.white[50]}
                    backgroundColor={PXBColors.blue[500]}
                    backgroundImage={headerBackgroundImage}
                    icon={<MenuIcon />}
                    onIconClick={(): void => toggleDrawer()}
                    titleContent={
                        <div style={{ width: '100%' }}>
                            <img style={{ maxWidth: 220, marginTop: 5 }} src={BrandLogo} alt="logo" />
                        </div>
                    }
                />
                <DrawerSubheader>
                    <div className={classes.background}>
                        <OrganizationDropdownToolbar />
                    </div>
                </DrawerSubheader>
                <DrawerBody itemFontColor={PXBColors.black[400]} itemIconColor={PXBColors.black[400]}>
                    <DrawerNavGroup
                        activeItemBackgroundColor={PXBColors.blue[50]}
                        activeItemIconColor={PXBColors.blue[500]}
                        activeItemFontColor={PXBColors.blue[500]}
                        activeItemBackgroundShape="round"
                        items={firstNavList}
                    />
                    {isRecordFound(userRole) && (
                        <>
                            <div style={{ flex: '1 0 0px' }} />
                            {hasNoOperation ? (
                                <DrawerNavGroup
                                    activeItemBackgroundColor={PXBColors.blue[50]}
                                    activeItemIconColor={PXBColors.blue[500]}
                                    activeItemFontColor={PXBColors.blue[500]}
                                    activeItemBackgroundShape="round"
                                    items={navListForSiteRole}
                                />
                            ) : (
                                <DrawerNavGroup
                                    activeItemBackgroundColor={PXBColors.blue[50]}
                                    activeItemIconColor={PXBColors.blue[500]}
                                    activeItemFontColor={PXBColors.blue[500]}
                                    activeItemBackgroundShape="round"
                                    items={secondNavList}
                                />
                            )}
                        </>
                    )}
                </DrawerBody>

                <DrawerFooter>
                    <div style={{ padding: '10px 18px 0' }}>
                        <img src={eatonlogo} alt={'Eaton Logo'} />
                    </div>
                    <div style={{ paddingLeft: 18, display: 'flex', justifyContent: 'space-between' }}>
                        <Typography variant="caption">v {config.version}</Typography>
                        <Typography variant="caption">{t('DRAWER.COPYRIGHT')}</Typography>
                    </div>
                    <Typography style={{ display: 'flex', justifyContent: 'flex-end' }} variant="caption">
                        {t('DRAWER.RIGHTS_RESERVED')}
                    </Typography>
                </DrawerFooter>
            </Drawer>
        </>
    );
};
